import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import apiClient from '../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../components/CustomPaginator';
import TeachersListSkeletonLoader from '../../skeletons/TeachersListSkeletonLoader';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import ViewTeacherModal from './parts/ViewTeacherModal';
import RegisterTeacherModal from './parts/RegisterTeacherModal';
import PasswordResetModal from '../Students/parts/PasswordResetModal';

interface TeacherData {
  id: number;
  user_id: number;
  name: string;
  gender: string;
  email_id: string;
  parentage: string;
  dob: string;
  address: string;
  contact_no: string;
  account_no: string;
  bank_name: string;
  ifsc_code: string;
  created_at: string;
  updated_at: string;
  profile_picture_url?: string;
  department?: string;
  user_status?: number;
  user_login?: number;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

type ViewMode = 'card' | 'list';

const TeachersList: React.FC = () => {
  const [teachers, setTeachers] = useState<TeacherData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [imageErrors, setImageErrors] = useState<Map<number, boolean>>(new Map());
  const [statusLoadingIds, setStatusLoadingIds] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  // ── Search & filter state ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');   // controlled input value
  const [genderFilter, setGenderFilter] = useState<string>(''); // '', 'Male', 'Female', 'Other'
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Blink fix ──────────────────────────────────────────────────────────────
  const [listKey, setListKey] = useState<number>(0);

  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 1,
  });

  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [registerModalOpen, setRegisterModalOpen] = useState<boolean>(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [selectedTeacherData, setSelectedTeacherData] = useState<TeacherData | null>(null);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordResetTarget, setPasswordResetTarget] = useState<TeacherData | null>(null);

  const menuRefs = useRef<{ [key: number]: Menu | null }>({});
  const toast = useRef<Toast>(null);

  // Re-fetch whenever page, search query, or gender filter changes
  useEffect(() => { fetchTeachers(); }, [currentPage, searchQuery, genderFilter]);

  const fetchTeachers = async (): Promise<void> => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: currentPage,
        limit: paginationData.per_page,
      };
      if (searchQuery.trim())  params.search = searchQuery.trim();
      if (genderFilter)        params.gender = genderFilter;

      const [response] = await Promise.all([
        apiClient.get('/teachers-list2', { params }),
        new Promise(resolve => setTimeout(resolve, 600)),
      ]);

      const data = response.data;
      if (data.status === 'success') {
        setTeachers(data.data);
        setPaginationData({
          current_page: data.pagination.current_page,
          per_page: data.pagination.per_page,
          total_items: data.pagination.total_items,
          total_pages: data.pagination.total_pages,
        });
        setListKey(k => k + 1);
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch teachers', life: 4000 });
    } finally {
      setLoading(false);
    }
  };

  // ── Debounced search handler ───────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);       // reset to page 1 on new search
      setSearchQuery(value);
    }, 420);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleGenderChange = (value: string) => {
    setGenderFilter(value);
    setCurrentPage(1);
  };

  const isFiltered = searchQuery.trim() !== '' || genderFilter !== '';

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleAddTeacher        = () => { setSelectedTeacherData(null); setRegisterModalOpen(true); };
  const handleEdit              = (t: TeacherData) => { setSelectedTeacherData(t); setRegisterModalOpen(true); };
  const handleView              = (t: TeacherData) => { setSelectedTeacherId(String(t.id)); setViewModalOpen(true); };
  const handleUploadPhoto       = (t: TeacherData) => { setSelectedTeacherData(t); setRegisterModalOpen(true); };
  const handleOpenPasswordReset = (t: TeacherData) => { setPasswordResetTarget(t); setIsPasswordModalOpen(true); };

  const handleRegistrationSuccess = (shouldResetPage?: boolean) => {
    if (shouldResetPage) setCurrentPage(1); else fetchTeachers();
  };

  const handlePasswordReset = useCallback(async (newPassword: string): Promise<void> => {
    if (!passwordResetTarget?.user_id) throw new Error('No teacher selected');
    await apiClient.post('/admin/reset-password', { user_id: passwordResetTarget.user_id, new_password: newPassword });
  }, [passwordResetTarget?.user_id]);

  const handleToggleStatus = async (teacher: TeacherData) => {
    const newStatus = teacher.user_status === 1 ? 0 : 1;
    const action = newStatus === 1 ? 'Activate' : 'Deactivate';
    setStatusLoadingIds(prev => new Set(prev).add(teacher.user_id));
    try {
      const response = await apiClient.put(`/users/${teacher.user_id}/status`, { user_status: newStatus });
      if (response.data.status === 'success') {
        setTeachers(prev => prev.map(t => t.user_id === teacher.user_id ? { ...t, user_status: newStatus } : t));
        toast.current?.show({
          severity: newStatus === 1 ? 'success' : 'warn',
          summary: `${action}d`,
          detail: `${teacher.name} has been ${action.toLowerCase()}d successfully.`,
          life: 3000,
        });
      } else {
        toast.current?.show({ severity: 'info', summary: 'No Change', detail: response.data.message, life: 3000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: err?.response?.data?.message ?? err?.message ?? `Failed to ${action.toLowerCase()} teacher`,
        life: 4000,
      });
    } finally {
      setStatusLoadingIds(prev => { const n = new Set(prev); n.delete(teacher.user_id); return n; });
    }
  };

  const getMenuItems = (teacher: TeacherData): MenuItem[] => {
    const isActive = teacher.user_status !== 0;
    const isStatusLoading = statusLoadingIds.has(teacher.user_id);
    return [
      { label: 'View Details',   icon: 'pi pi-eye',          command: () => handleView(teacher) },
      { label: 'Edit',           icon: 'pi pi-pencil',        command: () => handleEdit(teacher) },
      { label: 'Upload Photo',   icon: 'pi pi-camera',        command: () => handleUploadPhoto(teacher) },
      { label: 'Reset Password', icon: 'pi pi-key',           command: () => handleOpenPasswordReset(teacher), className: 'text-violet-600' },
      { separator: true },
      {
        label: isStatusLoading ? (isActive ? 'Deactivating…' : 'Activating…') : (isActive ? 'Deactivate' : 'Activate'),
        icon:  isStatusLoading ? 'pi pi-spin pi-spinner' : (isActive ? 'pi pi-ban' : 'pi pi-check-circle'),
        command: () => { if (!isStatusLoading) handleToggleStatus(teacher); },
        className: isActive ? 'text-red-500' : 'text-green-500',
      },
    ];
  };

  // ── Shared status pill ────────────────────────────────────────────────────
  const StatusPill = ({ teacher, small = false }: { teacher: TeacherData; small?: boolean }) => {
    const isActive = teacher.user_status !== 0;
    const isLoading = statusLoadingIds.has(teacher.user_id);
    return (
      <div
        className="status-pill"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: small ? '3px 10px' : '5px 13px',
          borderRadius: 30, userSelect: 'none', cursor: isLoading ? 'default' : 'pointer',
          transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
          background: isLoading ? 'rgba(245,158,11,0.09)' : isActive ? 'rgba(16,185,129,0.09)' : 'rgba(239,68,68,0.09)',
          border: `1px solid ${isLoading ? 'rgba(245,158,11,0.28)' : isActive ? 'rgba(16,185,129,0.28)' : 'rgba(239,68,68,0.28)'}`,
        }}
        onClick={() => { if (!isLoading) handleToggleStatus(teacher); }}
        title={isLoading ? 'Processing...' : isActive ? 'Click to deactivate' : 'Click to activate'}
      >
        {isLoading
          ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 9, color: '#f59e0b' }} />
          : <div className={isActive ? 'dot-pulse' : ''} style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? '#10b981' : '#ef4444' }} />
        }
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', transition: 'color 0.4s ease',
          color: isLoading ? '#f59e0b' : isActive ? '#10b981' : '#ef4444',
        }}>
          {isLoading ? (isActive ? 'Deactivating...' : 'Activating...') : isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
    );
  };

  // ── Avatar helper ─────────────────────────────────────────────────────────
  const Avatar = ({ teacher, size = 88 }: { teacher: TeacherData; size?: number }) => {
    const hasErr = imageErrors.get(teacher.user_id) || false;
    const isActive = teacher.user_status !== 0;
    const fontSize = size > 50 ? 30 : 15;
    return teacher.profile_picture_url && !hasErr ? (
      <img
        src={teacher.profile_picture_url} alt={teacher.name}
        onError={() => setImageErrors(prev => new Map(prev).set(teacher.user_id, true))}
        style={{
          width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
          border: `${size > 50 ? 3 : 2}px solid ${isActive ? 'rgba(65,105,225,0.2)' : 'rgba(239,68,68,0.2)'}`,
          boxShadow: isActive ? `0 4px 14px rgba(65,105,225,0.18)` : '0 4px 14px rgba(0,0,0,0.1)',
          filter: isActive ? 'none' : 'grayscale(55%) opacity(0.72)', transition: 'filter 0.4s ease',
        }}
      />
    ) : (
      <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: isActive ? 'linear-gradient(145deg,#4169E1,#6b8df5)' : 'linear-gradient(145deg,#b0b8c9,#d1d5db)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize, fontWeight: 700, color: 'white',
        border: `${size > 50 ? 3 : 2}px solid ${isActive ? 'rgba(65,105,225,0.2)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: isActive ? `0 4px 14px rgba(65,105,225,0.25)` : '0 4px 14px rgba(0,0,0,0.1)',
      }}>
        {teacher.name.charAt(0).toUpperCase()}
      </div>
    );
  };

  // ── Card ──────────────────────────────────────────────────────────────────
  const TeacherCard = ({ teacher, index }: { teacher: TeacherData; index: number }) => {
    const isActive = teacher.user_status !== 0;
    const isStatusLoading = statusLoadingIds.has(teacher.user_id);

    return (
      <div
        className="teacher-card"
        style={{
          '--delay': `${index * 0.048}s`,
          borderRadius: 22,
          border: `1.5px solid ${isActive ? '#eaecf2' : 'rgba(239,68,68,0.18)'}`,
          boxShadow: isActive
            ? '0 1px 6px rgba(65,105,225,0.06), 0 4px 18px rgba(0,0,0,0.05)'
            : '0 1px 6px rgba(239,68,68,0.06), 0 4px 18px rgba(0,0,0,0.04)',
          transition: 'box-shadow 0.45s cubic-bezier(0.22,1,0.36,1), transform 0.45s cubic-bezier(0.22,1,0.36,1), border-color 0.35s ease',
          height: '100%',
          background: isActive ? 'linear-gradient(160deg,#ffffff 60%,#f5f8ff 100%)' : 'linear-gradient(160deg,#fffafa 60%,#fff5f5 100%)',
          position: 'relative', overflow: 'hidden',
        } as React.CSSProperties}
        onMouseEnter={e => {
          const el = e.currentTarget;
          el.style.boxShadow = isActive
            ? '0 10px 36px rgba(65,105,225,0.15), 0 2px 8px rgba(65,105,225,0.08)'
            : '0 10px 36px rgba(239,68,68,0.12), 0 2px 8px rgba(239,68,68,0.07)';
          el.style.transform = 'translateY(-6px) scale(1.01)';
          el.style.borderColor = isActive ? 'rgba(65,105,225,0.28)' : 'rgba(239,68,68,0.3)';
        }}
        onMouseLeave={e => {
          const el = e.currentTarget;
          el.style.boxShadow = isActive
            ? '0 1px 6px rgba(65,105,225,0.06), 0 4px 18px rgba(0,0,0,0.05)'
            : '0 1px 6px rgba(239,68,68,0.06), 0 4px 18px rgba(0,0,0,0.04)';
          el.style.transform = 'translateY(0) scale(1)';
          el.style.borderColor = isActive ? '#eaecf2' : 'rgba(239,68,68,0.18)';
        }}
      >
        {/* Top shimmer band */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 120, pointerEvents: 'none',
          background: isActive
            ? 'linear-gradient(160deg,rgba(65,105,225,0.05) 0%,rgba(91,124,230,0.025) 60%,transparent 100%)'
            : 'linear-gradient(160deg,rgba(239,68,68,0.04) 0%,rgba(239,68,68,0.018) 60%,transparent 100%)',
          borderRadius: '22px 22px 60% 60%/22px 22px 28px 28px',
        }} />
        {/* Decorative bg circle */}
        <div style={{
          position: 'absolute', top: -28, right: -28, width: 90, height: 90,
          borderRadius: '50%', pointerEvents: 'none',
          background: isActive
            ? 'radial-gradient(circle,rgba(65,105,225,0.07) 0%,transparent 70%)'
            : 'radial-gradient(circle,rgba(239,68,68,0.06) 0%,transparent 70%)',
        }} />

        {/* 3-dot menu */}
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
          <Button
            icon="pi pi-ellipsis-v" rounded text severity="secondary"
            onClick={e => menuRefs.current[teacher.id]?.toggle(e)}
            style={{
              width: 30, height: 30, color: '#9ca3af',
              background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10,
              fontSize: 11, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          />
          <Menu
            model={getMenuItems(teacher)} popup
            ref={el => { if (el) menuRefs.current[teacher.id] = el; }}
            style={{ width: 186, borderRadius: 14 }}
          />
        </div>

        {/* ID badge */}
        <div style={{
          position: 'absolute', top: 12, left: 12, zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 4, padding: '4px 9px',
          borderRadius: 30, background: 'rgba(255,255,255,0.88)',
          border: '1px solid rgba(0,0,0,0.07)', backdropFilter: 'blur(10px)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <i className="pi pi-id-card" style={{ fontSize: 9, color: '#9ca3af' }} />
          <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.03em' }}>
            #{teacher.id}
          </span>
        </div>

        <div style={{ padding: '50px 22px 22px', textAlign: 'center', position: 'relative' }}>
          {/* Avatar with glow ring */}
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 14 }}>
            <div style={{
              position: 'absolute', inset: -5, borderRadius: '50%', pointerEvents: 'none',
              background: isActive
                ? 'radial-gradient(circle,rgba(65,105,225,0.13) 0%,transparent 70%)'
                : 'radial-gradient(circle,rgba(239,68,68,0.10) 0%,transparent 70%)',
            }} />
            <Avatar teacher={teacher} size={88} />
            {/* Status dot */}
            <div
              className={isStatusLoading ? 'dot-spin' : isActive ? 'dot-pulse' : ''}
              style={{
                position: 'absolute', bottom: 3, right: 3,
                width: 15, height: 15, borderRadius: '50%',
                background: isStatusLoading
                  ? 'linear-gradient(135deg,#f59e0b,#fbbf24)'
                  : isActive
                    ? 'linear-gradient(135deg,#10b981,#34d399)'
                    : 'linear-gradient(135deg,#ef4444,#f87171)',
                border: '2.5px solid white',
                boxShadow: isActive && !isStatusLoading
                  ? '0 2px 6px rgba(16,185,129,0.45)'
                  : '0 2px 6px rgba(239,68,68,0.4)',
                transition: 'background 0.5s ease',
              }}
            />
          </div>

          {/* Name */}
          <h3 style={{
            margin: '0 0 7px', fontSize: 16, fontWeight: 700,
            letterSpacing: '-0.025em', lineHeight: 1.25, transition: 'color 0.4s ease',
            color: isActive ? '#1e2940' : '#a0aab8',
          }}>
            {teacher.name}
          </h3>

          {/* Department badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 11px', borderRadius: 30, marginBottom: 10,
            background: 'linear-gradient(135deg,rgba(65,105,225,0.09),rgba(91,124,230,0.06))',
            border: '1px solid rgba(65,105,225,0.13)',
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#4169E1', boxShadow: '0 0 5px rgba(65,105,225,0.5)' }} />
            <span style={{ fontSize: 11, color: '#4169E1', fontWeight: 600, letterSpacing: '0.025em' }}>
              {teacher.department || 'Teacher'}
            </span>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 11px', borderRadius: 30, marginBottom: 10,
            background: 'linear-gradient(135deg,rgba(65,105,225,0.09),rgba(91,124,230,0.06))',
            border: '1px solid rgba(65,105,225,0.13)', marginLeft: 7
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#4169E1', boxShadow: '0 0 5px rgba(65,105,225,0.5)' }} />
            <span style={{ fontSize: 11, color: '#4169E1', fontWeight: 600, letterSpacing: '0.025em' }}>
              {teacher.user_login || 'Teacher'}
            </span>
          </div>

          {/* Status pill */}
          <div style={{ marginBottom: 16 }}>
            <StatusPill teacher={teacher} />
          </div>

          {/* Info rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, paddingTop: 15, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            {([
              { icon: 'pi pi-envelope', value: teacher.email_id,  mono: false },
              { icon: 'pi pi-phone',    value: teacher.contact_no, mono: true },
            ] as const).map(({ icon, value, mono }) => (
              <div
                key={icon}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 11px', borderRadius: 12,
                  background: 'rgba(248,249,252,0.85)', border: '1px solid rgba(0,0,0,0.04)',
                  transition: 'all 0.28s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(65,105,225,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(65,105,225,0.18)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(248,249,252,0.85)';
                  e.currentTarget.style.borderColor = 'rgba(0,0,0,0.04)';
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'linear-gradient(145deg,rgba(65,105,225,0.1),rgba(91,124,230,0.07))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className={icon} style={{ fontSize: 11, color: '#4169E1' }} />
                </div>
                <span style={{
                  flex: 1, textAlign: 'left', fontWeight: 500, fontSize: 12, color: '#4b5563',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontFamily: mono ? 'monospace' : 'inherit',
                }}>
                  {value}
                </span>
              </div>
            ))}

            {/* User ID */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 11px', borderRadius: 12,
              background: 'rgba(248,249,252,0.85)', border: '1px solid rgba(0,0,0,0.04)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="pi pi-user" style={{ fontSize: 11, color: '#9ca3af' }} />
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>User ID</span>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#4169E1', fontFamily: 'monospace',
                padding: '2px 9px', borderRadius: 7, background: 'rgba(65,105,225,0.08)', letterSpacing: '0.02em',
              }}>
                #{teacher.user_id}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Table column renderers ────────────────────────────────────────────────
  const serialTemplate = (_: TeacherData, opts: ColumnBodyOptions) => {
    const n = opts.rowIndex + (currentPage - 1) * paginationData.per_page + 1;
    return (
      <div style={{
        width: 28, height: 28, background: '#f9fafb', borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 12, color: '#6b7280', margin: '0 auto',
      }}>{n}</div>
    );
  };

  const avatarNameTemplate = (t: TeacherData) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Avatar teacher={t} size={38} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {t.name}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.gender}</div>
      </div>
    </div>
  );

  const deptTemplate   = (t: TeacherData) => (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20,
      background: 'rgba(65,105,225,0.08)', border: '1px solid rgba(65,105,225,0.13)',
    }}>
      <span style={{ fontSize: 11, color: '#4169E1', fontWeight: 600 }}>{t.department || 'Teacher'}</span>
    </div>
  );
  const emailTemplate  = (t: TeacherData) => (
    <span style={{ fontSize: 12, color: '#4b5563', fontWeight: 500 }}>{t.email_id}</span>
  );
  const phoneTemplate  = (t: TeacherData) => (
    <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace', fontWeight: 500 }}>{t.contact_no}</span>
  );
  const statusTemplate = (t: TeacherData) => <StatusPill teacher={t} small />;

  const actionTemplate = (t: TeacherData) => (
    <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
      {([
        { icon: 'pi pi-eye',    color: '#60a5fa', tip: 'View',           fn: () => handleView(t) },
        { icon: 'pi pi-pencil', color: '#3b82f6', tip: 'Edit',           fn: () => handleEdit(t) },
        { icon: 'pi pi-key',    color: '#8b5cf6', tip: 'Reset Password', fn: () => handleOpenPasswordReset(t) },
      ] as const).map(({ icon, color, tip, fn }) => (
        <Button
          key={icon} icon={icon}
          className="p-button-text p-button-rounded"
          tooltip={tip} tooltipOptions={{ position: 'top' }}
          style={{ color, width: 32, height: 32 }}
          onClick={fn}
        />
      ))}
    </div>
  );

  // ── Empty state ───────────────────────────────────────────────────────────
  const emptyContent = (
    <div style={{
      background: 'linear-gradient(160deg,#ffffff 60%,#f8faff 100%)',
      borderRadius: 22, padding: '90px 20px', textAlign: 'center',
      boxShadow: '0 2px 16px rgba(0,0,0,0.05)', border: '1.5px solid rgba(65,105,225,0.1)',
    }}>
      <div style={{
        width: 60, height: 60, borderRadius: 16, margin: '0 auto 22px',
        background: isFiltered
          ? 'linear-gradient(145deg,#f59e0b,#fbbf24)'
          : 'linear-gradient(145deg,#4169E1,#6b8df5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: isFiltered ? '0 6px 20px rgba(245,158,11,0.28)' : '0 6px 20px rgba(65,105,225,0.25)',
      }}>
        <i className={isFiltered ? 'pi pi-search' : 'pi pi-users'} style={{ fontSize: 26, color: 'white' }} />
      </div>
      <h3 style={{ margin: '0 0 10px', color: isFiltered ? '#f59e0b' : '#4169E1', fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>
        {isFiltered ? 'No Results Found' : 'No Teachers Found'}
      </h3>
      <p style={{ margin: '0 auto', color: '#94a3b8', fontSize: 14, lineHeight: 1.65, maxWidth: 360 }}>
        {isFiltered
          ? `No teachers match your current search or filters. Try adjusting your query.`
          : 'Start by adding your first teacher using the button above.'
        }
      </p>
      {isFiltered && (
        <button
          onClick={() => { handleSearchClear(); handleGenderChange(''); }}
          style={{
            marginTop: 18, padding: '8px 20px', borderRadius: 10,
            background: 'linear-gradient(135deg,rgba(245,158,11,0.1),rgba(251,191,36,0.07))',
            border: '1px solid rgba(245,158,11,0.25)', color: '#f59e0b',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(245,158,11,0.15)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'linear-gradient(135deg,rgba(245,158,11,0.1),rgba(251,191,36,0.07)')}
        >
          <i className="pi pi-times" style={{ fontSize: 11, marginRight: 6 }} />
          Clear filters
        </button>
      )}
    </div>
  );

  // ── Gender filter chips ───────────────────────────────────────────────────
  const genderOptions = [
    { label: 'All',    value: '',       color: '#4169E1' },
    { label: 'Male',   value: 'Male',   color: '#3b82f6' },
    { label: 'Female', value: 'Female', color: '#ec4899' },
    { label: 'Other',  value: 'Other',  color: '#8b5cf6' },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        /* ── Entrance animations ─────────────────────────────────────────── */
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes rowIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        @keyframes searchBarIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0);    }
        }

        .teacher-card {
          animation: cardIn 0.52s cubic-bezier(0.22,1,0.36,1) both;
          animation-delay: var(--delay, 0s);
          will-change: transform, opacity;
        }
        .search-bar-wrapper {
          animation: searchBarIn 0.4s cubic-bezier(0.22,1,0.36,1) both;
        }

        /* ── Pulse / spin helpers ────────────────────────────────────────── */
        @keyframes softPulse {
          0%,100% { opacity:1;   transform:scale(1);    }
          50%      { opacity:0.7; transform:scale(0.85); }
        }
        @keyframes dotSpin { to { transform:rotate(360deg); } }

        .dot-pulse { animation: softPulse 2.6s ease-in-out infinite; }
        .dot-spin  { animation: dotSpin   1s   linear       infinite; }

        /* ── Search input ────────────────────────────────────────────────── */
        .tl-search-input {
          width: 100%;
          background: transparent;
          border: none;
          outline: none;
          font-size: 13.5px;
          font-weight: 500;
          color: #1e2940;
          font-family: inherit;
        }
        .tl-search-input::placeholder { color: #b0bac9; }

        .tl-search-wrap {
          display: flex; align-items: center; gap: 10;
          padding: 0 14px; height: 42px; flex: 1; min-width: 0;
          background: white;
          border: 1.5px solid #e5e9f2;
          border-radius: 12px;
          box-shadow: 0 1px 4px rgba(65,105,225,0.06);
          transition: border-color 0.22s ease, box-shadow 0.22s ease;
        }
        .tl-search-wrap:focus-within {
          border-color: #4169E1;
          box-shadow: 0 0 0 3px rgba(65,105,225,0.10), 0 1px 4px rgba(65,105,225,0.08);
        }

        /* ── Gender chips ────────────────────────────────────────────────── */
        .gender-chip {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 13px; border-radius: 20px;
          font-size: 12px; font-weight: 600; cursor: pointer;
          border: 1.5px solid; transition: all 0.22s cubic-bezier(0.22,1,0.36,1);
          white-space: nowrap;
        }

        /* ── Status pill ─────────────────────────────────────────────────── */
        .status-pill:hover { filter:brightness(1.05); transform:translateY(-1px); }

        /* ── DataTable rows ──────────────────────────────────────────────── */
        .p-datatable .p-datatable-tbody > tr {
          animation: rowIn 0.32s ease-out both;
          transition: background 0.15s;
        }
        .p-datatable .p-datatable-tbody > tr:hover {
          background: rgba(65,105,225,0.04) !important;
          box-shadow: inset 4px 0 0 0 #4169E1;
        }
        .p-datatable .p-datatable-thead > tr > th {
          background: #f9fafb !important; color: #6b7280 !important;
          font-size: 11px !important; font-weight: 700 !important;
          text-transform: uppercase !important; letter-spacing: .8px !important;
          padding: 16px 12px !important; border-bottom: 1px solid #e5e7eb !important;
        }
        .p-datatable .p-datatable-tbody > tr > td {
          padding: 14px 12px !important; border-bottom: 1px solid #f3f4f6 !important;
        }
        .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom: none !important; }
        .p-button-text:hover { background: #f3f4f6 !important; }

        /* ── PrimeReact Menu ─────────────────────────────────────────────── */
        .p-menu {
          border-radius: 14px !important; padding: 6px !important;
          border: 1px solid rgba(0,0,0,0.07) !important;
          box-shadow: 0 8px 28px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06) !important;
        }
        .p-menu .p-menuitem-link {
          padding: 8px 11px !important; border-radius: 9px !important;
          margin: 2px 0 !important; transition: all 0.2s ease !important;
        }
        .p-menu .p-menuitem-link:hover  { background: rgba(65,105,225,0.06) !important; }
        .p-menu .p-menuitem-icon        { color: #8492a6 !important; font-size: 12px !important; margin-right: 9px !important; }
        .p-menu .p-menuitem-text        { font-size: 13px !important; font-weight: 500 !important; color: #374151 !important; }
        .p-menu .p-menuitem-separator   { margin: 5px 4px !important; border-top: 1px solid rgba(0,0,0,0.06) !important; }

        .text-red-500    .p-menuitem-link { color: #ef4444 !important; }
        .text-red-500    .p-menuitem-link:hover { background: rgba(239,68,68,0.07) !important; }
        .text-red-500    .p-menuitem-icon,
        .text-red-500    .p-menuitem-text  { color: #ef4444 !important; }

        .text-green-500  .p-menuitem-link { color: #10b981 !important; }
        .text-green-500  .p-menuitem-link:hover { background: rgba(16,185,129,0.07) !important; }
        .text-green-500  .p-menuitem-icon,
        .text-green-500  .p-menuitem-text  { color: #10b981 !important; }

        .text-violet-600 .p-menuitem-link { color: #7c3aed !important; }
        .text-violet-600 .p-menuitem-link:hover { background: rgba(124,58,237,0.07) !important; }
        .text-violet-600 .p-menuitem-icon,
        .text-violet-600 .p-menuitem-text  { color: #7c3aed !important; }

        /* ── View toggle ─────────────────────────────────────────────────── */
        .view-toggle-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 14px; border-radius: 10px; font-size: 12px;
          font-weight: 600; cursor: pointer; border: 1.5px solid;
          transition: all 0.22s cubic-bezier(0.22,1,0.36,1);
          background: none;
        }
        .view-toggle-btn.active   { background: #4169E1; color: white; border-color: #4169E1; box-shadow: 0 3px 10px rgba(65,105,225,0.28); }
        .view-toggle-btn.inactive { background: white; color: #4169E1; border-color: #c7d2fe; }
        .view-toggle-btn.inactive:hover { background: #eff6ff; }

        /* ── Responsive grid ─────────────────────────────────────────────── */
        @media (max-width:1280px) { .teachers-grid { grid-template-columns: repeat(3,1fr) !important; } }
        @media (max-width:900px)  { .teachers-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width:560px)  { .teachers-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <GlobalHeaderTitle
          title="Teachers Management"
          subtitle={`${paginationData.total_items} total teachers`}
          icon="pi pi-users"
          buttons={[{ label: 'Add Teacher', icon: 'pi pi-plus', onClick: handleAddTeacher }]}
        />

        {/* ── Search & Filter Bar ──────────────────────────────────────────── */}
        <div
          className="search-bar-wrapper"
          style={{
            background: 'linear-gradient(135deg,#ffffff,#f9fbff)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 16,
            padding: '14px 18px',
            marginBottom: 16,
            boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Row 1: search input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="tl-search-wrap" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', height: 42, flex: 1 }}>
              {/* Search icon — shows spinner while loading */}
              <div style={{ width: 18, flexShrink: 0 }}>
                {loading && searchInput
                  ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 14, color: '#4169E1' }} />
                  : <i className="pi pi-search"          style={{ fontSize: 14, color: searchInput ? '#4169E1' : '#b0bac9' }} />
                }
              </div>

              <input
                className="tl-search-input"
                type="text"
                placeholder="Search by name, email or phone…"
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') handleSearchClear();
                }}
              />

              {/* Clear button */}
              {searchInput && (
                <button
                  onClick={handleSearchClear}
                  style={{
                    background: 'rgba(65,105,225,0.08)', border: 'none',
                    borderRadius: 6, width: 22, height: 22, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#4169E1', transition: 'background 0.18s ease',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(65,105,225,0.16)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'rgba(65,105,225,0.08)')}
                  title="Clear search"
                >
                  <i className="pi pi-times" style={{ fontSize: 9 }} />
                </button>
              )}
            </div>

            {/* Active filter indicator badge */}
            {isFiltered && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 10, flexShrink: 0,
                background: 'linear-gradient(135deg,rgba(245,158,11,0.1),rgba(251,191,36,0.07))',
                border: '1px solid rgba(245,158,11,0.25)',
              }}>
                <i className="pi pi-filter-fill" style={{ fontSize: 11, color: '#f59e0b' }} />
                <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700 }}>Filtered</span>
              </div>
            )}
          </div>

          {/* Row 2: gender chips + result count */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            {/* Gender filter chips */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 2 }}>
                Filter:
              </span>
              {genderOptions.map(opt => {
                const isActive = genderFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    className="gender-chip"
                    onClick={() => handleGenderChange(opt.value)}
                    style={{
                      borderColor: isActive ? opt.color : 'rgba(0,0,0,0.1)',
                      color:       isActive ? 'white'   : opt.color,
                      background:  isActive
                        ? `linear-gradient(135deg,${opt.color},${opt.color}dd)`
                        : 'white',
                      boxShadow: isActive ? `0 3px 10px ${opt.color}33` : 'none',
                      transform: isActive ? 'translateY(-1px)' : 'none',
                    }}
                  >
                    {opt.value === ''       && <i className="pi pi-users"  style={{ fontSize: 10 }} />}
                    {opt.value === 'Male'   && <i className="pi pi-mars"   style={{ fontSize: 10 }} />}
                    {opt.value === 'Female' && <i className="pi pi-venus"  style={{ fontSize: 10 }} />}
                    {opt.value === 'Other'  && <i className="pi pi-circle" style={{ fontSize: 10 }} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Result count pill */}
            {(searchQuery || genderFilter) && !loading && (
              <div style={{
                fontSize: 12, color: '#64748b', fontWeight: 600,
                padding: '4px 12px', borderRadius: 8,
                background: 'rgba(65,105,225,0.06)', border: '1px solid rgba(65,105,225,0.1)',
              }}>
                <i className="pi pi-list" style={{ fontSize: 10, marginRight: 5, color: '#4169E1' }} />
                {paginationData.total_items} result{paginationData.total_items !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <TeachersListSkeletonLoader />
        ) : teachers.length > 0 ? (
          <>
            {/* ── Info / controls bar ──────────────────────────────────────── */}
            <div style={{
              borderRadius: 14, padding: '14px 18px', marginBottom: 20,
              boxShadow: '0 1px 6px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: 'wrap', gap: 10,
              background: 'linear-gradient(135deg,#ffffff,#f9fbff)',
            }}>
              {/* Count */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: 'linear-gradient(145deg,rgba(65,105,225,0.1),rgba(91,124,230,0.07))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(65,105,225,0.12)',
                }}>
                  <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
                </div>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  Showing{' '}
                  <span style={{ color: '#1e2940' }}>
                    {((currentPage - 1) * paginationData.per_page) + 1}–{Math.min(currentPage * paginationData.per_page, paginationData.total_items)}
                  </span>
                  {' '}of{' '}
                  <span style={{ color: '#1e2940' }}>{paginationData.total_items}</span> teachers
                  {isFiltered && (
                    <span style={{ color: '#f59e0b', marginLeft: 4 }}>(filtered)</span>
                  )}
                </span>
              </div>

              {/* View toggle + page badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className={`view-toggle-btn ${viewMode === 'card' ? 'active' : 'inactive'}`} onClick={() => setViewMode('card')}>
                    <i className="pi pi-th-large" style={{ fontSize: 12 }} /> Cards
                  </button>
                  <button className={`view-toggle-btn ${viewMode === 'list' ? 'active' : 'inactive'}`} onClick={() => setViewMode('list')}>
                    <i className="pi pi-list" style={{ fontSize: 12 }} /> List
                  </button>
                </div>
                <div style={{
                  padding: '6px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg,rgba(65,105,225,0.09),rgba(91,124,230,0.06))',
                  border: '1px solid rgba(65,105,225,0.13)',
                }}>
                  <span style={{ fontSize: 12, color: '#4169E1', fontWeight: 700 }}>
                    Page {paginationData.current_page} / {paginationData.total_pages}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Card grid ────────────────────────────────────────────────── */}
            {viewMode === 'card' && (
              <div
                key={`cards-${listKey}`}
                className="teachers-grid"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 32 }}
              >
                {teachers.map((t, i) => <TeacherCard key={t.id} teacher={t} index={i} />)}
              </div>
            )}

            {/* ── List / DataTable ──────────────────────────────────────────── */}
            {viewMode === 'list' && (
              <div
                key={`list-${listKey}`}
                style={{
                  background: 'white', borderRadius: 20, marginBottom: 32,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}
              >
                <DataTable value={teachers} loading={false} emptyMessage="">
                  <Column header="#"          body={serialTemplate}     style={{ width: 52 }} />
                  <Column header="Name"       body={avatarNameTemplate} style={{ minWidth: 180 }} />
                  <Column header="Department" body={deptTemplate}       style={{ width: 150 }} />
                  <Column header="Email"      body={emailTemplate}      style={{ minWidth: 160 }} />
                  <Column header="Phone"      body={phoneTemplate}      style={{ width: 140 }} />
                  <Column header="Status"     body={statusTemplate}     style={{ width: 130 }} />
                  <Column header="Actions"    body={actionTemplate}     style={{ width: 130 }} />
                </DataTable>
              </div>
            )}

            {paginationData.total_pages > 1 && (
              <CustomPaginator
                page={paginationData.current_page}
                totalPages={paginationData.total_pages}
                totalRecords={paginationData.total_items}
                pageSize={paginationData.per_page}
                onPageChange={newPage => setCurrentPage(newPage)}
                showInfoBar={false}
              />
            )}
          </>
        ) : emptyContent}
      </div>

      <ViewTeacherModal
        open={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setSelectedTeacherId(null); }}
        teacherId={selectedTeacherId}
      />
      <RegisterTeacherModal
        open={registerModalOpen}
        onClose={() => { setRegisterModalOpen(false); setSelectedTeacherData(null); }}
        onSuccess={handleRegistrationSuccess}
        teacherData={selectedTeacherData}
      />
      <PasswordResetModal
        visible={isPasswordModalOpen}
        onClose={() => { setIsPasswordModalOpen(false); setPasswordResetTarget(null); }}
        onSubmit={handlePasswordReset}
        studentName={passwordResetTarget?.name ?? ''}
      />
    </>
  );
};

export default TeachersList;